import CategoriesTable from "../components/categories/CategoriesTable";
import EmptyState from "../components/shared/EmptyState";
import ErrorState from "../components/shared/ErrorState";
import LoadingState from "../components/shared/LoadingState";
import { CategoryResult } from "../interfaces/ICompetitions";
import { Container } from "@mui/material";
import { useCompetitionContext } from "../contexts/CompetitionContext";
import { useMemo, useState } from "react";
import { useNavigationHelper } from "../hooks/useNavigationHelper";
import {
  hasVisibleFinalResults,
  hasVisibleRoundResults,
  isCompetitionVisibleInResults,
} from "../utils/resultPublicationUtils";

interface CategoriesListProps {
  mode?: "rounds" | "final-results";
}

export default function CategoriesList({ mode = "rounds" }: CategoriesListProps) {
  const { competition, loading, error, refreshCompetition } = useCompetitionContext();
  const { goToRounds, goToCompetitions, goToFinalResults } = useNavigationHelper();
  const [selectedDays, setSelectedDays] = useState<number[]>([]);
  const visibleCategories = useMemo(
    () =>
      mode === "final-results"
        ? (competition?.categories || []).filter((category) => hasVisibleFinalResults(category))
        : (competition?.categories || []).filter((category) =>
            competition ? hasVisibleRoundResults(competition, category) : false,
          ),
    [competition, mode],
  );
  const availableDays = useMemo(
    () =>
      mode === "final-results"
        ? Array.from(
            new Set(
              visibleCategories
                .map((category) => category.day)
                .filter((day): day is number => typeof day === "number"),
            ),
          ).sort((dayA, dayB) => dayA - dayB)
        : [],
    [mode, visibleCategories],
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <LoadingState message="Ładowanie kategorii..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorState message={error} onRetry={refreshCompetition} />
      </Container>
    );
  }

  if (!competition) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <EmptyState message="Nie znaleziono turnieju" />
      </Container>
    );
  }

  if (!isCompetitionVisibleInResults(competition)) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <ErrorState message="Dla tego turnieju nie opublikowano wyników" />
      </Container>
    );
  }

  const handleToggleDay = (day: number) => {
    setSelectedDays((previousDays) =>
      previousDays.includes(day)
        ? previousDays.filter((previousDay) => previousDay !== day)
        : previousDays.concat(day).sort((dayA, dayB) => dayA - dayB),
    );
  };

  const handleRowClick = (category: CategoryResult) => {
    if (mode === "final-results") {
      goToFinalResults(competition.id, category.categoryId);
      return;
    }

    goToRounds(competition.id, category.categoryId);
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <CategoriesTable
        categories={visibleCategories}
        competitionId={competition.id}
        onRowClick={handleRowClick}
        onBack={goToCompetitions}
        emptyMessage={
          mode === "final-results"
            ? "Brak opublikowanych wyników końcowych"
            : "Brak opublikowanych wyników eliminacji"
        }
        availableDays={mode === "final-results" ? availableDays : undefined}
        selectedDays={mode === "final-results" ? selectedDays : undefined}
        onToggleDay={mode === "final-results" ? handleToggleDay : undefined}
      />
    </Container>
  );
}
